import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import VerifiedRounded from '@mui/icons-material/VerifiedRounded';
import { useAuth } from '@/auth';
import { useNotify } from '@/components/feedback/notificationsContext';
import { UserAvatar } from '@/components/UserAvatar';
import { updateProfile } from '@/platform/api';
import { useSession } from '@/platform/session';
import { ROLE_LABEL } from '@/platform/types';
import { formatDateTime } from '@/utils/format';
import { PageHeader } from './PageHeader';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} sx={{ py: 1.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: { sm: 180 }, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</Box>
    </Stack>
  );
}

export function AccountPage() {
  const { user: authUser, signOut } = useAuth();
  const { user, role, refresh, me } = useSession();
  const notify = useNotify();
  const [name, setName] = useState(user.name ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile(name.trim());
      await refresh();
      notify('Nome atualizado.', 'success');
    } catch {
      notify('Não foi possível salvar.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Minha conta" subtitle="Seus dados na plataforma." />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <UserAvatar
                user={authUser}
                sx={{ width: 88, height: 88, mx: 'auto', fontSize: '2rem', mb: 2 }}
              />
              <Typography variant="h5">{user.name ?? 'Sem nome'}</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {user.email}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ justifyContent: 'center', flexWrap: 'wrap' }}
              >
                <Chip size="small" variant="soft" color="primary" label={ROLE_LABEL[role]} />
                {user.emailVerified && (
                  <Chip
                    size="small"
                    variant="soft"
                    color="lime"
                    icon={<VerifiedRounded />}
                    label="E-mail verificado"
                  />
                )}
              </Stack>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutRounded />}
                sx={{ mt: 3 }}
                onClick={() => void signOut()}
              >
                Sair
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Perfil
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  label="Nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={() => void save()}
                  disabled={saving || !name.trim() || name.trim() === user.name}
                  sx={{ flexShrink: 0 }}
                >
                  {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
                </Button>
              </Stack>
              <Divider />
              {user.institutions.length > 0 && (
                <>
                  <Row label={user.institutions.length > 1 ? 'Instituições' : 'Instituição'}>
                    {user.institutions.map((i) => i.name).join(', ')}
                  </Row>
                  <Divider />
                </>
              )}
              {user.course && (
                <>
                  <Row label="Curso">{user.course.name}</Row>
                  <Divider />
                </>
              )}
              {user.institutionalEmail && (
                <>
                  <Row label="E-mail institucional">
                    {user.institutionalEmail ?? '—'}
                    {user.institutionalEmailVerified && (
                      <Chip
                        size="small"
                        variant="soft"
                        color="lime"
                        icon={<VerifiedRounded />}
                        label="Confirmado"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Row>
                  <Divider />
                </>
              )}
              <Row label="Matrícula">{user.registration ?? '—'}</Row>
              <Divider />
              <Row label="Turmas">
                {user.groups.length ? user.groups.map((g) => g.name).join(', ') : '—'}
              </Row>
              <Divider />
              <Row label="Formas de entrar">
                {[user.hasGoogle && 'Google', user.hasPassword && 'E-mail e senha']
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </Row>
              <Divider />
              <Row label="Último acesso">{formatDateTime(user.lastLoginAt)}</Row>
              <Divider />
              <Row label="Plataforma">
                {me.app.name} v{me.app.version}
              </Row>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
